import React from "react";
import i18next from "i18next";
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import { billingService, isPremiumOrder, readPaymentOrder, savePaymentOrder,
  paymentPrice, premiumUntilDate } from "../_services/billing.service";
import { hasAlipayPaymentBridge, startAlipayPayment } from "../_helpers/paymentBridge";
import { responseErrorToText } from "../_helpers/error";

export default class PremiumPurchaseDialog extends React.Component {
  state = { order: null, busy: false, nativePending: false, error: "", interrupted: false };
  generation = 0;
  busy = false;

  componentDidMount() {
    if (this.props.open) this.initialize();
    window.addEventListener("focus", this.onResume);
    document.addEventListener("visibilitychange", this.onResume);
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && (!prevProps.open || prevProps.conferenceId !== this.props.conferenceId)) {
      this.initialize();
    } else if (!this.props.open && prevProps.open) {
      this.generation += 1;
      this.cancelNative?.();
    }
  }

  componentWillUnmount() {
    this.generation += 1;
    this.cancelNative?.();
    window.removeEventListener("focus", this.onResume);
    document.removeEventListener("visibilitychange", this.onResume);
  }

  initialize = () => {
    this.generation += 1;
    this.cancelNative?.();
    this.busy = false;
    this.reportedPaid = null;
    const stored = readPaymentOrder("premium", this.props.userId, this.props.conferenceId);
    this.orderId = this.props.initialOrderId
      || (isPremiumOrder(stored, this.props.conferenceId) && stored.status === "created" ? stored.order_id : null);
    this.setState({ order: null, error: "", busy: false, nativePending: false,
      interrupted: this.props.paymentResult === "fail" }, () => {
      if (this.orderId) this.checkOrder();
    });
  };

  onResume = () => {
    if (this.props.open && document.visibilityState !== "hidden" && this.orderId
      && this.state.order?.status !== "paid") this.checkOrder();
  };

  acceptOrder = (order, preserveError = false) => {
    if (!isPremiumOrder(order, this.props.conferenceId)
      || (this.orderId && Number(order.order_id) !== Number(this.orderId))) {
      this.setState({ error: i18next.t("premium_order_mismatch") });
      return false;
    }
    this.orderId = order.order_id;
    savePaymentOrder("premium", this.props.userId, order, this.props.conferenceId);
    this.setState({ order, ...(!preserveError || order.status === "paid" ? { error: "" } : {}) });
    this.props.onOrder?.(order);
    if (order.status === "paid" && this.reportedPaid !== order.order_id) {
      this.reportedPaid = order.order_id;
      this.setState({ nativePending: false });
      this.cancelNative?.();
      this.props.onPaid(order);
    }
    return true;
  };

  checkOrder = (preserveError = false) => {
    if (this.busy || !this.orderId) return;
    const generation = this.generation;
    this.busy = true;
    this.setState({ busy: true, ...(preserveError === true ? {} : { error: "" }) });
    billingService.getOrder(this.orderId, (order) => {
      if (generation !== this.generation) return;
      this.busy = false;
      this.setState({ busy: false });
      this.acceptOrder(order, preserveError === true);
    }, (error) => {
      if (generation !== this.generation) return;
      this.busy = false;
      this.setState({ busy: false, error: responseErrorToText(error) });
    });
  };

  pay = () => {
    if (this.busy || this.state.nativePending || !this.props.verified) return;
    if (process.env.REACT_APP_CHINA_MODE === "true" && !hasAlipayPaymentBridge()) {
      this.setState({ error: i18next.t("premium_update_app") });
      return;
    }
    const generation = this.generation;
    this.busy = true;
    this.setState({ busy: true, error: "", interrupted: false });
    billingService.createPremiumOrder(this.props.conferenceId, (order) => {
      if (generation !== this.generation) return;
      this.busy = false;
      this.setState({ busy: false });
      if (!this.acceptOrder(order) || order.status === "paid") return;
      if (order.status !== "created") return;
      if (order.redirect) {
        window.location.assign(order.redirect);
      } else if (order.order_string) {
        try {
          this.setState({ nativePending: true });
          this.cancelNative = startAlipayPayment(order.order_string, (result) => {
            if (generation !== this.generation) return;
            this.setState({ nativePending: false,
              interrupted: !!result.error || !["9000", "8000", "6004"].includes(result.result_status) });
            this.checkOrder();
          });
        } catch (error) {
          this.setState({ nativePending: false, error: i18next.t(
            error.message === "premium_update_app" ? "premium_update_app" : "premium_payment_start_error") });
          this.checkOrder(true);
        }
      } else {
        this.setState({ error: i18next.t("premium_payment_start_error") });
      }
    }, (error) => {
      if (generation !== this.generation) return;
      this.busy = false;
      this.setState({ busy: false, error: responseErrorToText(error) });
    });
  };

  render() {
    const { open, onClose, premiumState, created, verified } = this.props;
    const { order, busy, nativePending, error, interrupted } = this.state;
    const paid = order?.status === "paid";
    const pending = order?.status === "created";
    const quote = order || { cost: premiumState.price, currency: premiumState.currency, months: premiumState.months };
    const canPay = !paid && (!order || pending) && premiumState.status === "ok";
    return (
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm"
        sx={{ zIndex: 10000 }} PaperProps={{ style: { padding: 0 } }}>
        <DialogTitle>{i18next.t(created && !order ? "premium_created_title" : "premium")}</DialogTitle>
        <DialogContent style={{ width: "100%", padding: "8px 24px", boxSizing: "border-box", overflowWrap: "anywhere" }}>
          <p style={{ lineHeight: 1.6 }}>{i18next.t("premium_explanation")}</p>
          {quote.months != null && paymentPrice(quote.cost, quote.currency) && (
            <p style={{ marginTop: 16, fontWeight: 600 }}>{i18next.t("premium_offer_terms", {
              price: paymentPrice(quote.cost, quote.currency), months: quote.months,
            })}</p>
          )}
          <div aria-live="polite" style={{ marginTop: 16, lineHeight: 1.6 }}>
            {paid && <p>{i18next.t("premium_until", { date: premiumUntilDate(order.premium_until) })}</p>}
            {pending && <p>{i18next.t(interrupted ? "premium_payment_interrupted" : "premium_payment_waiting")}</p>}
            {order && !pending && !paid && <p>{i18next.t("premium_payment_other_status")}</p>}
            {!verified && !paid && <p>{i18next.t("premium_verify_account")}</p>}
            {error && <p role="alert" style={{ color: "#d32f2f" }}>{error}</p>}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: 24, gap: 12, flexWrap: "wrap" }}>
          {busy && <CircularProgress size={24} aria-label={i18next.t("premium_check_payment")} />}
          <button type="button" className="btn btn--border" disabled={busy} onClick={onClose}>
            {i18next.t(paid ? "premium_close" : "premium_later")}
          </button>
          {this.orderId && !paid && (
            <button type="button" className="btn btn--border" disabled={busy} onClick={this.checkOrder}>
              {i18next.t("premium_check_payment")}
            </button>
          )}
          {canPay && (
            <button type="button" className="btn" disabled={busy || nativePending || !verified} onClick={this.pay}>
              {i18next.t(pending ? "premium_retry_payment" : "premium_go_to_payment")}
            </button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}
